/**
 * Parse a user-selected JSON file into a plain object (IntentML import, exports, etc.).
 */

export class JsonFileParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JsonFileParseError';
    Object.setPrototypeOf(this, JsonFileParseError.prototype);
  }
}

/** Works in browsers and Jest/jsdom where `File.prototype.text` may be missing or broken. */
function readFileAsText(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text().catch(() => readFileAsTextViaReader(file));
  }
  return readFileAsTextViaReader(file);
}

function readFileAsTextViaReader(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new JsonFileParseError('Could not read file.'));
    reader.readAsText(file);
  });
}

export async function parseJsonFileToRecord(file: File): Promise<Record<string, unknown>> {
  let text: string;
  try {
    text = await readFileAsText(file);
  } catch (e: unknown) {
    if (e instanceof JsonFileParseError) throw e;
    throw new JsonFileParseError('Could not read file.');
  }
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new JsonFileParseError('JSON root must be an object.');
    }
    return parsed as Record<string, unknown>;
  } catch (e: unknown) {
    if (e instanceof JsonFileParseError) throw e;
    throw new JsonFileParseError('Invalid JSON file.');
  }
}
